import React, { useEffect, useState, Children, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, SlidersHorizontal, Star, MapPin, MessageSquare, Video, ChevronDown, Zap, Clock, BookOpen, LayoutGrid, FileText, Sparkles, PlayCircle, ArrowRight, List, PlusCircle, ShoppingCart } from 'lucide-react';
import { AdSlot } from '../components/ads/AdSlot';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { SearchBar } from '../components/ui/SearchBar';
import { Rating } from '../components/ui/Rating';
import { Pagination } from '../components/ui/Pagination';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { useToast } from '../components/ui/Toast';
import { useCart, type CartItem } from '../contexts/CartContext';
interface ServicesMarketplacePageProps {
  onNavigate: (page: string) => void;
}
/* ── Animation Variants ── */
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ServicesMarketplacePage({
  onNavigate
}: ServicesMarketplacePageProps) {
  const [activeCategory, setActiveCategory] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceFrom, setPriceFrom] = useState('');
  const [priceTo, setPriceTo] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('popular');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const {
    addItem
  } = useCart();
  const {
    success
  } = useToast();
  const ITEMS_PER_PAGE = 8;
  const getCategoryType = (category: string): CartItem['type'] => {
    switch (category) {
      case 'Консультации':
        return 'consultation';
      case 'Курсы и марафоны':
        return 'course';
      case 'Письменные разборы':
        return 'premium-report';
      default:
        return 'marketplace-product';
    }
  };
  const handleAddToCart = (service: (typeof services)[0]) => {
    const priceNum = parseInt(service.price.replace(/[^\d]/g, ''));
    addItem({
      id: `marketplace-${service.id}-${Date.now()}`,
      name: service.title,
      description: service.desc,
      price: priceNum,
      type: getCategoryType(service.category),
      format: service.format,
      duration: service.duration,
      astrologer: {
        name: service.astrologer,
        initials: service.initials,
        rating: service.rating,
        reviews: service.reviews
      },
      badges: [{
        label: service.category,
        variant: 'pro' as const
      }]
    });
    success(`«${service.title}» добавлено в корзину`);
  };
  // Default to list on mobile
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    if (mq.matches) setViewMode('list');
    const handler = (e: MediaQueryListEvent) => setViewMode(e.matches ? 'list' : 'grid');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Close sort dropdown on outside click
  useEffect(() => {
    if (!showSortDropdown) return;
    const handler = () => setShowSortDropdown(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showSortDropdown]);
  // Helper: parse price string to number
  const parsePrice = (p: string) => Number(p.replace(/[^\d]/g, ''));
  // Helper: get duration category
  const getDurationCategory = (duration: string): string => {
    const lower = duration.toLowerCase();
    if (lower.includes('мин') || lower.includes('час') && !lower.includes('2')) return 'До 1 часа';
    if (lower.includes('1.5 час') || lower.includes('2 час')) return '1-2 часа';
    if (lower.includes('день') || lower.includes('дня') || lower.includes('дней')) {
      const num = parseInt(duration);
      if (num && num <= 3) return '1-3 дня';
      if (num && num > 7) return 'Более недели';
      return '1-3 дня';
    }
    if (lower.includes('недел')) return 'Более недели';
    return '';
  };
  // Helper: check format match
  const matchesFormat = (serviceFormat: string, filterFormat: string): boolean => {
    const sf = serviceFormat.toLowerCase();
    const ff = filterFormat.toLowerCase();
    if (ff === 'видеозвонок') return sf.includes('видеозвонок');
    if (ff === 'текстовый чат') return sf.includes('текстовый чат');
    if (ff === 'pdf-файл') return sf.includes('pdf');
    if (ff === 'аудиосообщение') return sf.includes('аудио');
    if (ff === 'онлайн-курс') return sf.includes('курс') || sf.includes('telegram');
    return false;
  };
  const sortOptions = [{
    id: 'popular',
    label: 'По популярности'
  }, {
    id: 'price-asc',
    label: 'По цене ↑'
  }, {
    id: 'price-desc',
    label: 'По цене ↓'
  }, {
    id: 'rating',
    label: 'По рейтингу'
  }];
  const toggleFormat = (fmt: string) => {
    setSelectedFormats(prev => prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]);
    setCurrentPage(1);
  };
  const toggleDuration = (dur: string) => {
    setSelectedDurations(prev => prev.includes(dur) ? prev.filter(d => d !== dur) : [...prev, dur]);
    setCurrentPage(1);
  };
  const resetFilters = () => {
    setPriceFrom('');
    setPriceTo('');
    setSelectedFormats([]);
    setSelectedDurations([]);
    setSearchQuery('');
    setSortBy('popular');
    setActiveCategory('Все');
    setCurrentPage(1);
  };
  const categories = [{
    id: 'Все',
    icon: LayoutGrid
  }, {
    id: 'Консультации',
    icon: MessageSquare
  }, {
    id: 'Курсы и марафоны',
    icon: BookOpen
  }, {
    id: 'Вебинары',
    icon: Video
  }, {
    id: 'Письменные разборы',
    icon: FileText
  }];
  // Mock Data for Promoted Services
  const promotedServices = [{
    id: 'p1',
    astrologer: 'Елена Смирнова',
    avatarColor: 'bg-indigo-100 text-indigo-700',
    initials: 'ЕС',
    title: 'Полный разбор натальной карты + Прогноз на год',
    desc: 'Глубокий анализ вашей личности и подробный прогноз событий на ближайшие 12 месяцев.',
    price: '8 500 ₽',
    oldPrice: '10 000 ₽',
    rating: 4.9,
    reviews: 124,
    format: 'Видеозвонок',
    duration: '90 мин',
    gradient: 'from-indigo-50 to-white',
    accent: 'text-indigo-500',
    illustrationBg: 'bg-indigo-100'
  }, {
    id: 'p2',
    astrologer: 'Михаил Волков',
    avatarColor: 'bg-emerald-100 text-emerald-700',
    initials: 'МВ',
    title: 'Курс: Основы ведической астрологии',
    desc: 'Научитесь читать свою карту за 4 недели. Практические задания и сертификат.',
    price: '15 000 ₽',
    rating: 5.0,
    reviews: 89,
    format: 'Онлайн-курс',
    duration: '4 недели',
    gradient: 'from-emerald-50 to-white',
    accent: 'text-emerald-500',
    illustrationBg: 'bg-emerald-100'
  }, {
    id: 'p3',
    astrologer: 'Анна Коваль',
    avatarColor: 'bg-amber-100 text-amber-700',
    initials: 'АК',
    title: 'Марафон: Проработка Сатурна за 21 день',
    desc: 'Ежедневные практики для снятия блоков и выхода на новый уровень дохода.',
    price: '6 900 ₽',
    oldPrice: '9 000 ₽',
    rating: 4.8,
    reviews: 203,
    format: 'Telegram',
    duration: '21 день',
    gradient: 'from-amber-50 to-white',
    accent: 'text-amber-500',
    illustrationBg: 'bg-amber-100'
  }, {
    id: 'p4',
    astrologer: 'София Романова',
    avatarColor: 'bg-blue-100 text-blue-700',
    initials: 'СР',
    title: 'Вебинар: Затмения 2025 — что ждать?',
    desc: 'Разбор влияния коридора затмений на каждый знак зодиака.',
    price: '1 490 ₽',
    rating: 4.7,
    reviews: 67,
    format: 'Запись',
    duration: '2 часа',
    gradient: 'from-blue-50 to-white',
    accent: 'text-blue-500',
    illustrationBg: 'bg-blue-100'
  }, {
    id: 'p5',
    astrologer: 'Дмитрий Орлов',
    avatarColor: 'bg-rose-100 text-rose-700',
    initials: 'ДО',
    title: 'Финансовая астрология: когда покупать и продавать',
    desc: 'Индивидуальный разбор финансовых периодов на ближайший год.',
    price: '7 500 ₽',
    rating: 5.0,
    reviews: 156,
    format: 'Видеозвонок',
    duration: '60 мин',
    gradient: 'from-rose-50 to-white',
    accent: 'text-rose-500',
    illustrationBg: 'bg-rose-100'
  }, {
    id: 'p6',
    astrologer: 'Александр Ким',
    avatarColor: 'bg-purple-100 text-purple-700',
    initials: 'АК',
    title: 'Кармический разбор: прошлые жизни и задачи души',
    desc: 'Глубокий анализ Лунных узлов, Лилит и ретроградных планет.',
    price: '5 500 ₽',
    rating: 4.9,
    reviews: 98,
    format: 'PDF + аудио',
    duration: '5 дней',
    gradient: 'from-purple-50 to-white',
    accent: 'text-purple-500',
    illustrationBg: 'bg-purple-100'
  }];
  // Diverse Mock Data for Regular Services
  const services = [{
    id: 1,
    astrologer: 'Анна Коваль',
    avatarColor: 'bg-amber-100 text-amber-700',
    initials: 'АК',
    title: 'Консультация: Финансовый потенциал',
    desc: 'Разбор ваших сильных сторон для заработка, блокирующих установок и удачных ниш.',
    category: 'Консультации',
    price: '4 500 ₽',
    rating: 4.8,
    reviews: 42,
    format: 'Видеозвонок',
    duration: '60 мин'
  }, {
    id: 2,
    astrologer: 'Дмитрий Орлов',
    avatarColor: 'bg-rose-100 text-rose-700',
    initials: 'ДО',
    title: 'Письменный разбор: Синастрия (Совместимость)',
    desc: 'Подробный PDF-отчет о совместимости с партнером: точки роста, конфликты и кармические связи.',
    category: 'Письменные разборы',
    price: '3 000 ₽',
    rating: 4.9,
    reviews: 128,
    format: 'PDF-файл',
    duration: '3-4 дня'
  }, {
    id: 3,
    astrologer: 'София Романова',
    avatarColor: 'bg-blue-100 text-blue-700',
    initials: 'СР',
    title: 'Вебинар: Как пережить ретроградный Меркурий',
    desc: 'Запись практического вебинара с инструкциями и компенсаторными действиями на период ретрограда.',
    category: 'Вебинары',
    price: '990 ₽',
    rating: 4.7,
    reviews: 56,
    format: 'Запись',
    duration: '2 часа'
  }, {
    id: 4,
    astrologer: 'Александр Ким',
    avatarColor: 'bg-purple-100 text-purple-700',
    initials: 'АК',
    title: 'Детский гороскоп: таланты и кружки',
    desc: 'Помогу понять природные склонности вашего ребенка и подобрать идеальные секции для развития.',
    category: 'Письменные разборы',
    price: '3 500 ₽',
    rating: 5.0,
    reviews: 84,
    format: 'PDF-файл',
    duration: '3-4 дня'
  }, {
    id: 5,
    astrologer: 'Елена Смирнова',
    avatarColor: 'bg-indigo-100 text-indigo-700',
    initials: 'ЕС',
    title: 'Хорарный вопрос (ответ да/нет)',
    desc: 'Быстрый и точный ответ на один конкретный вопрос (покупка, переезд, отношения).',
    category: 'Консультации',
    price: '1 500 ₽',
    rating: 4.9,
    reviews: 215,
    format: 'Текстовый чат',
    duration: '1 день'
  }, {
    id: 6,
    astrologer: 'Михаил Волков',
    avatarColor: 'bg-emerald-100 text-emerald-700',
    initials: 'МВ',
    title: 'Марафон: Проработка Венеры',
    desc: '14 дней практик для раскрытия женственности, улучшения отношений и финансовой емкости.',
    category: 'Курсы и марафоны',
    price: '5 000 ₽',
    rating: 4.8,
    reviews: 312,
    format: 'Telegram-канал',
    duration: '14 дней'
  }, {
    id: 7,
    astrologer: 'Анна Коваль',
    avatarColor: 'bg-amber-100 text-amber-700',
    initials: 'АК',
    title: 'Консультация: Предназначение и карьера',
    desc: 'Поиск жизненного пути, анализ кармических узлов и выбор профессии по душе.',
    category: 'Консультации',
    price: '5 500 ₽',
    rating: 4.9,
    reviews: 67,
    format: 'Видеозвонок',
    duration: '90 мин'
  }, {
    id: 8,
    astrologer: 'София Романова',
    avatarColor: 'bg-blue-100 text-blue-700',
    initials: 'СР',
    title: 'Вебинар: Лунные циклы и планирование',
    desc: 'Как использовать фазы Луны для эффективного планирования дел и отдыха.',
    category: 'Вебинары',
    price: '1 200 ₽',
    rating: 4.6,
    reviews: 34,
    format: 'Запись + PDF',
    duration: '1.5 часа'
  }, {
    id: 9,
    astrologer: 'Дмитрий Орлов',
    avatarColor: 'bg-rose-100 text-rose-700',
    initials: 'ДО',
    title: 'Соляр: Прогноз на личный год',
    desc: 'Подробный разбор тенденций вашего года от дня рождения до дня рождения.',
    category: 'Письменные разборы',
    price: '4 000 ₽',
    rating: 5.0,
    reviews: 145,
    format: 'PDF-файл',
    duration: '5 дней'
  }];
  const filteredServices = services.filter(s => {
    // Category filter
    if (activeCategory !== 'Все' && s.category !== activeCategory) return false;
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!s.title.toLowerCase().includes(q) && !s.astrologer.toLowerCase().includes(q) && !s.desc.toLowerCase().includes(q)) return false;
    }
    // Price filter
    const price = parsePrice(s.price);
    if (priceFrom && price < Number(priceFrom)) return false;
    if (priceTo && price > Number(priceTo)) return false;
    // Format filter
    if (selectedFormats.length > 0 && !selectedFormats.some(fmt => matchesFormat(s.format, fmt))) return false;
    // Duration filter
    if (selectedDurations.length > 0 && !selectedDurations.includes(getDurationCategory(s.duration))) return false;
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return parsePrice(a.price) - parsePrice(b.price);
      case 'price-desc':
        return parsePrice(b.price) - parsePrice(a.price);
      case 'rating':
        return b.rating - a.rating;
      case 'popular':
      default:
        return b.reviews - a.reviews;
    }
  });
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredServices.length / ITEMS_PER_PAGE));
  const paginatedServices = filteredServices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery, priceFrom, priceTo, selectedFormats, selectedDurations, sortBy]);
  // Helper to get category accent color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Консультации':
        return 'border-t-indigo-500';
      case 'Курсы и марафоны':
        return 'border-t-emerald-500';
      case 'Вебинары':
        return 'border-t-amber-500';
      case 'Письменные разборы':
        return 'border-t-rose-500';
      default:
        return 'border-t-slate-300';
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-25450">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-indigo-50/80 via-white to-slate-50 border-b border-slate-200 pt-8 pb-12 overflow-hidden" data-id="element-25451">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-96 h-96 bg-indigo-100/50 rounded-full blur-3xl pointer-events-none" data-id="element-25452" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-64 h-64 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" data-id="element-25453" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-25454">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Маркетплейс услуг'
        }]} className="mb-8" data-id="element-25455" />
          <div className="flex flex-col gap-6" data-id="element-25456">
            {/* Title — full width */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" data-id="element-25457">
              Маркетплейс услуг
            </motion.h1>

            {/* Description + Search (left) | CTA card (right, full height) */}
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-5" data-id="element-25458">
              <div className="flex-1 flex flex-col gap-5" data-id="element-25459">
                <motion.p initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1
              }} className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-25460">
                  Все предложения проверенных астрологов в одном месте —
                  консультации, курсы, вебинары и подробные письменные разборы.
                </motion.p>
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2
              }} data-id="element-25461">
                  <SearchBar placeholder="Поиск по названию услуги или астрологу..." className="max-w-2xl shadow-sm" value={searchQuery} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} data-id="element-25462" />
                </motion.div>
              </div>

              {/* CTA — stretches full height of desc+search */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} className="flex-shrink-0 hidden lg:flex" data-id="element-25463">
                <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer group flex items-center" onClick={() => onNavigate('dashboard-astrologer')} data-id="element-25464">
                  <div className="absolute -top-8 -right-8 w-28 h-28 bg-white/10 rounded-full" data-id="element-25465" />
                  <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-white/5 rounded-full" data-id="element-25466" />

                  <div className="relative z-10 px-6 py-5 flex items-center gap-5" data-id="element-25467">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-25468">
                      <PlusCircle className="w-5 h-5 text-white" data-id="element-25469" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-25470">
                      <h3 className="text-sm font-bold text-white mb-0.5" data-id="element-25471">
                        Вы астролог?
                      </h3>
                      <p className="text-xs text-indigo-200 leading-snug" data-id="element-25472">
                        Добавьте свои услуги
                      </p>
                    </div>
                    <button onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onNavigate('dashboard-astrologer');
                  }} className="flex items-center gap-1.5 px-5 py-2.5 bg-white text-indigo-700 rounded-xl text-sm font-bold hover:bg-indigo-50 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-25473">
                      Добавить продукт
                      <ArrowRight className="w-4 h-4" data-id="element-25474" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-25475">
        {/* Category Pills */}
        <div className="flex overflow-x-auto pb-4 mb-8 gap-3 hide-scrollbar relative" data-id="element-25476">
          {categories.map(category => {
          const Icon = category.icon;
          const isActive = activeCategory === category.id;
          return <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`relative whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 z-10 ${isActive ? 'text-indigo-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`} data-id="element-25477">
                {isActive && <motion.div layoutId="activeCategory" className="absolute inset-0 bg-indigo-100 rounded-full -z-10" transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-25478" />}
                <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-25479" />
                {category.id}
              </button>;
        })}
        </div>

        {/* Promoted Services (Only show on 'Все' or if matches category) */}
        {activeCategory === 'Все' && <div className="mb-10" data-id="element-25480">
            <div className="flex items-center justify-between mb-4" data-id="element-25481">
              <div className="flex items-center gap-2" data-id="element-25482">
                <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-25483">
                  <Zap className="w-3.5 h-3.5 text-amber-600" data-id="element-25484" />
                </div>
                <h2 className="text-lg font-bold text-slate-900" data-id="element-25485">
                  Рекомендуемое
                </h2>
                <span className="text-xs text-slate-400 ml-1" data-id="element-25486">
                  {promotedServices.length}
                </span>
              </div>
              <span className="text-xs text-slate-400 hidden sm:block" data-id="element-25487">
                ← прокрутите →
              </span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 hide-scrollbar snap-x snap-mandatory -mx-4 px-4" data-id="element-25488">
              {promotedServices.map(service => <motion.div key={service.id} whileHover={{
            y: -2
          }} transition={{
            duration: 0.2
          }} className="snap-start flex-shrink-0 w-72" data-id="element-25489">
                  <div className={`bg-gradient-to-br ${service.gradient} border border-amber-200/60 rounded-xl p-4 cursor-pointer group hover:shadow-md transition-shadow h-full flex flex-col`} onClick={() => onNavigate('product-detail')} data-id="element-25490">
                    <div className="flex items-center justify-between mb-3" data-id="element-25491">
                      <Badge variant="warning" className="bg-amber-100 text-amber-800 border-none text-[10px] px-2 py-0.5" data-id="element-25492">
                        Промо
                      </Badge>
                      <div className="flex items-center gap-1.5" data-id="element-25493">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${service.avatarColor}`} data-id="element-25494">
                          {service.initials}
                        </div>
                        <span className="text-xs font-medium text-slate-600 truncate max-w-[100px]" data-id="element-25495">
                          {service.astrologer}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-1 line-clamp-2 group-hover:text-indigo-600 transition-colors leading-snug" data-id="element-25496">
                      {service.title}
                    </h3>
                    <p className="text-xs text-slate-500 mb-3 line-clamp-1 flex-grow" data-id="element-25497">
                      {service.desc}
                    </p>
                    <div className="flex items-center gap-2 mb-3 text-[11px] text-slate-500" data-id="element-25498">
                      <span className="flex items-center" data-id="element-25499">
                        <Video className="w-3 h-3 mr-1 text-slate-400" data-id="element-25500" />{' '}
                        {service.format}
                      </span>
                      <span className="flex items-center" data-id="element-25501">
                        <Clock className="w-3 h-3 mr-1 text-slate-400" data-id="element-25502" />{' '}
                        {service.duration}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-200/50" data-id="element-25503">
                      <div className="flex items-center gap-1.5" data-id="element-25504">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-25505" />
                        <span className="text-xs font-medium text-slate-700" data-id="element-25506">
                          {service.rating}
                        </span>
                        <span className="text-[10px] text-slate-400" data-id="element-25507">
                          ({service.reviews})
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-25508">
                        {service.oldPrice && <span className="text-[11px] text-slate-400 line-through" data-id="element-25509">
                            {service.oldPrice}
                          </span>}
                        <span className="text-sm font-bold text-slate-900" data-id="element-25510">
                          {service.price}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>}

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-25511">
          {/* Sidebar Filters */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-25512">
            <div className="sticky top-24 space-y-6" data-id="element-25513">
              <Card padding="md" className="border-slate-200 shadow-sm" data-id="element-25514">
                <div className="flex items-center justify-between mb-2 lg:mb-6 pb-2 lg:pb-4 border-b border-slate-100" data-id="element-25515">
                  <button className="font-bold text-slate-900 flex items-center lg:cursor-default focus:outline-none" onClick={() => setShowMobileFilters(!showMobileFilters)} data-id="element-25516">
                    <SlidersHorizontal className="w-4 h-4 mr-2 text-slate-400" data-id="element-25517" />{' '}
                    Фильтры
                    <ChevronDown className={`w-4 h-4 ml-2 lg:hidden transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} data-id="element-25518" />
                  </button>
                  <button onClick={resetFilters} className={`text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline ${showMobileFilters ? '' : 'hidden lg:inline'}`} data-id="element-25519">
                    Сбросить
                  </button>
                </div>

                <div className={`space-y-8 ${showMobileFilters ? '' : 'hidden lg:block'}`} data-id="element-25520">
                  {/* Price Range */}
                  <div data-id="element-25521">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-25522">
                      Цена
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-25523">
                      <Input placeholder="От" type="number" className="w-full text-sm" value={priceFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceFrom(e.target.value)} data-id="element-25524" />
                      <span className="text-slate-400" data-id="element-25525">-</span>
                      <Input placeholder="До" type="number" className="w-full text-sm" value={priceTo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPriceTo(e.target.value)} data-id="element-25526" />
                    </div>
                  </div>

                  {/* Format */}
                  <div data-id="element-25527">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-25528">
                      Формат
                    </h3>
                    <div className="space-y-2.5" data-id="element-25529">
                      {['Видеозвонок', 'Текстовый чат', 'PDF-файл', 'Аудиосообщение', 'Онлайн-курс'].map(fmt => <label key={fmt} className="flex items-center group cursor-pointer" data-id="element-25530">
                          <input type="checkbox" checked={selectedFormats.includes(fmt)} onChange={() => toggleFormat(fmt)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer" data-id="element-25531" />
                          <span className="ml-3 text-sm text-slate-600 group-hover:text-slate-900 transition-colors" data-id="element-25532">
                            {fmt}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Duration */}
                  <div data-id="element-25533">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-25534">
                      Длительность / Срок
                    </h3>
                    <div className="space-y-2.5" data-id="element-25535">
                      {['До 1 часа', '1-2 часа', '1-3 дня', 'Более недели'].map(dur => <label key={dur} className="flex items-center group cursor-pointer" data-id="element-25536">
                            <input type="checkbox" checked={selectedDurations.includes(dur)} onChange={() => toggleDuration(dur)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer" data-id="element-25537" />
                            <span className="ml-3 text-sm text-slate-600 group-hover:text-slate-900 transition-colors" data-id="element-25538">
                              {dur}
                            </span>
                          </label>)}
                    </div>
                  </div>
                </div>
              </Card>

              {/* ── Vertical Ad Banner (sidebar) ── */}
              <div className="hidden lg:block" data-id="element-25539">
                <AdSlot slotId="SLOT-19" onNavigate={onNavigate} data-id="element-25540" />
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="flex-1" data-id="element-25541">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-id="element-25542">
              <span className="text-slate-600 font-medium" data-id="element-25543">
                Найдено: {filteredServices.length} услуг
              </span>
              <div className="flex items-center gap-3" data-id="element-25544">
                {/* View toggle */}
                <div className="flex items-center bg-white rounded-lg border border-slate-200 shadow-sm p-0.5" data-id="element-25545">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:text-slate-600'}`} title="Карточки" data-id="element-25546">
                    <LayoutGrid className="w-4 h-4" data-id="element-25547" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:text-slate-600'}`} title="Список" data-id="element-25548">
                    <List className="w-4 h-4" data-id="element-25549" />
                  </button>
                </div>
                <div className="relative flex items-center bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm" data-id="element-25550">
                  <span className="text-sm text-slate-500 mr-2" data-id="element-25551">
                    Сортировка:
                  </span>
                  <button onClick={e => {
                  e.stopPropagation();
                  setShowSortDropdown(!showSortDropdown);
                }} className="text-sm font-medium text-slate-900 flex items-center hover:text-indigo-600 transition-colors" data-id="element-25552">
                    {sortOptions.find(o => o.id === sortBy)?.label}{' '}
                    <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} data-id="element-25553" />
                  </button>
                  {showSortDropdown && <div className="absolute top-full right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg py-1 z-50 min-w-[180px]" data-id="element-25554">
                      {sortOptions.map(option => <button key={option.id} onClick={e => {
                    e.stopPropagation();
                    setSortBy(option.id);
                    setShowSortDropdown(false);
                  }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${sortBy === option.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-25555">
                          {option.label}
                        </button>)}
                    </div>}
                </div>
              </div>
            </div>

            {/* ── Grid View ── */}
            {viewMode === 'grid' && paginatedServices.length > 0 && <motion.div key={`grid-${activeCategory}-${currentPage}`} variants={staggerContainer} initial="hidden" animate="visible" className="mb-12" data-id="element-25556">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-25557">
                  {paginatedServices.map((service, idx) => <Fragment key={service.id} data-id="element-25558">
                      <motion.div variants={staggerItem} className="h-full" data-id="element-25559">
                        <div className={`flex flex-col h-full bg-white rounded-xl border border-slate-200 border-t-[3px] ${getCategoryColor(service.category)} shadow-sm cursor-pointer group hover:shadow-md transition-shadow p-4`} onClick={() => onNavigate('product-detail')} data-id="element-25560">
                          <div className="flex items-center gap-2.5 mb-3" data-id="element-25561">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${service.avatarColor}`} data-id="element-25562">
                              {service.initials}
                            </div>
                            <div className="min-w-0" data-id="element-25563">
                              <div className="text-sm font-bold text-slate-900 truncate" data-id="element-25564">
                                {service.astrologer}
                              </div>
                              <Rating value={service.rating} count={service.reviews} size="sm" data-id="element-25565" />
                            </div>
                          </div>

                          <h3 className="text-sm font-bold text-slate-900 mb-1.5 group-hover:text-indigo-600 transition-colors line-clamp-2 leading-snug" data-id="element-25566">
                            {service.title}
                          </h3>

                          <p className="text-xs text-slate-500 mb-3 flex-grow line-clamp-2" data-id="element-25567">
                            {service.desc}
                          </p>

                          <div className="flex items-center gap-3 mb-3 text-xs text-slate-500" data-id="element-25568">
                            <span className="flex items-center" data-id="element-25569">
                              <BookOpen className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-25570" />{' '}
                              {service.format}
                            </span>
                            <span className="flex items-center" data-id="element-25571">
                              <Clock className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-25572" />{' '}
                              {service.duration}
                            </span>
                          </div>

                          <div className="pt-3 border-t border-slate-100 flex items-center justify-between" data-id="element-25573">
                            <div className="text-base font-bold text-slate-900" data-id="element-25574">
                              {service.price}
                            </div>
                            <button onClick={e => {
                        e.stopPropagation();
                        handleAddToCart(service);
                      }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-semibold hover:bg-indigo-100 transition-colors" data-id="element-25575">
                              <ShoppingCart className="w-3.5 h-3.5" data-id="element-25576" />В корзину
                            </button>
                          </div>
                        </div>
                      </motion.div>

                      {/* Ad banner after every 4th item */}
                      {(idx + 1) % 4 === 0 && idx < paginatedServices.length - 1 && <motion.div variants={staggerItem} className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4" data-id="element-25577">
                            <AdSlot slotId="SLOT-16" onNavigate={onNavigate} data-id="element-25578" />
                          </motion.div>}
                    </Fragment>)}
                </div>
              </motion.div>}

            {/* ── List / Table View ── */}
            {viewMode === 'list' && paginatedServices.length > 0 && <motion.div key={`list-${activeCategory}`} variants={staggerContainer} initial="hidden" animate="visible" className="mb-12" data-id="element-25579">
                {/* Table header (desktop only) */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2.5 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-200 mb-1" data-id="element-25580">
                  <div className="col-span-5" data-id="element-25581">Услуга</div>
                  <div className="col-span-2" data-id="element-25582">Формат</div>
                  <div className="col-span-2" data-id="element-25583">Срок</div>
                  <div className="col-span-1" data-id="element-25584">Рейтинг</div>
                  <div className="col-span-2 text-right" data-id="element-25585">Цена</div>
                </div>

                {paginatedServices.map((service, idx) => <Fragment key={service.id} data-id="element-25586">
                    <motion.div variants={staggerItem} className="group cursor-pointer" onClick={() => onNavigate('product-detail')} data-id="element-25587">
                      {/* Desktop row */}
                      <div className="hidden md:grid md:grid-cols-12 gap-4 items-center px-4 py-3.5 bg-white border border-slate-100 rounded-lg mb-2 hover:shadow-md hover:border-slate-200 transition-all" data-id="element-25588">
                        <div className="col-span-5 flex items-center gap-3 min-w-0" data-id="element-25589">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${service.avatarColor}`} data-id="element-25590">
                            {service.initials}
                          </div>
                          <div className="min-w-0" data-id="element-25591">
                            <div className="text-sm font-bold text-slate-900 truncate group-hover:text-indigo-600 transition-colors" data-id="element-25592">
                              {service.title}
                            </div>
                            <div className="text-xs text-slate-500 truncate" data-id="element-25593">
                              {service.astrologer} · {service.category}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2 text-sm text-slate-600 flex items-center" data-id="element-25594">
                          <BookOpen className="w-3.5 h-3.5 mr-1.5 text-slate-400 flex-shrink-0" data-id="element-25595" />{' '}
                          {service.format}
                        </div>
                        <div className="col-span-2 text-sm text-slate-600 flex items-center" data-id="element-25596">
                          <Clock className="w-3.5 h-3.5 mr-1.5 text-slate-400 flex-shrink-0" data-id="element-25597" />{' '}
                          {service.duration}
                        </div>
                        <div className="col-span-1 flex items-center gap-1" data-id="element-25598">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-25599" />
                          <span className="text-sm font-medium text-slate-700" data-id="element-25600">
                            {service.rating}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2" data-id="element-25601">
                          <span className="text-base font-bold text-slate-900" data-id="element-25602">
                            {service.price}
                          </span>
                          <button onClick={e => {
                      e.stopPropagation();
                      handleAddToCart(service);
                    }} className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center hover:bg-indigo-100 transition-colors flex-shrink-0" title="В корзину" data-id="element-25603">
                            <ShoppingCart className="w-4 h-4" data-id="element-25604" />
                          </button>
                        </div>
                      </div>

                      {/* Mobile row */}
                      <div className="md:hidden flex items-center gap-3 px-3 py-3 bg-white border border-slate-100 rounded-lg mb-2 hover:shadow-sm transition-shadow" data-id="element-25605">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${service.avatarColor}`} data-id="element-25606">
                          {service.initials}
                        </div>
                        <div className="flex-grow min-w-0" data-id="element-25607">
                          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-25608">
                            {service.title}
                          </div>
                          <div className="text-xs text-slate-500 truncate" data-id="element-25609">
                            {service.astrologer} · {service.format} ·{' '}
                            {service.duration}
                          </div>
                          <div className="flex items-center gap-2 mt-1" data-id="element-25610">
                            <div className="flex items-center gap-0.5" data-id="element-25611">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-25612" />
                              <span className="text-xs font-medium text-slate-600" data-id="element-25613">
                                {service.rating}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400" data-id="element-25614">
                              ({service.reviews})
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-25615">
                          <span className="text-sm font-bold text-slate-900" data-id="element-25616">
                            {service.price}
                          </span>
                          <button onClick={e => {
                      e.stopPropagation();
                      handleAddToCart(service);
                    }} className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center hover:bg-indigo-100 transition-colors" title="В корзину" data-id="element-25617">
                            <ShoppingCart className="w-3.5 h-3.5" data-id="element-25618" />
                          </button>
                        </div>
                      </div>

                      {/* Horizontal ad banner after every 4th item in list */}
                      {(idx + 1) % 4 === 0 && idx < paginatedServices.length - 1 && <motion.div variants={staggerItem} className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4" data-id="element-25619">
                            <AdSlot slotId="SLOT-16" onNavigate={onNavigate} data-id="element-25620" />
                          </motion.div>}
                    </motion.div>
                  </Fragment>)}
              </motion.div>}

            {/* Empty state */}
            {paginatedServices.length === 0 && <div className="flex flex-col items-center justify-center py-20 mb-12" data-id="element-25621">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-25622">
                  <Search className="w-7 h-7 text-slate-400" data-id="element-25623" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-25624">
                  Ничего не найдено
                </h3>
                <p className="text-sm text-slate-500 mb-6 text-center max-w-sm" data-id="element-25625">
                  Попробуйте изменить фильтры или поисковый запрос
                </p>
                <Button variant="outline" onClick={resetFilters} data-id="element-25626">
                  Сбросить все фильтры
                </Button>
              </div>}

            {paginatedServices.length > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-25627" />}
          </div>
        </div>
      </div>
    </div>;
}